import {
  type ChangeEventHandler, useCallback, useEffect, useState,
} from 'react';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import {
  CONFIG, getLocalStorageEnvironmentConfig, writeLocalStorageConfig,
} from '~/_shared/constants/config';
import { noop } from '~/_shared/utils/function.helpers';

interface StandardFeatureToggleProps {
  featureName: keyof typeof CONFIG;
  label: string;
}

const isFeatureToggleEnabled = (
  featureName: keyof typeof CONFIG,
  localStorageConfig: ReturnType<typeof getLocalStorageEnvironmentConfig>,
): boolean => {
  return !!CONFIG[featureName] || !!localStorageConfig[featureName];
};

const isFeatureToggleEnabledByDefault = (
  featureName: keyof typeof CONFIG,
  localStorageConfig: ReturnType<typeof getLocalStorageEnvironmentConfig>,
): boolean => {
  return !!CONFIG[featureName] && !localStorageConfig[featureName];
};

const enableFeature = (
  featureName: keyof typeof CONFIG,
  localStorageConfig: ReturnType<typeof getLocalStorageEnvironmentConfig>,
) => {
  writeLocalStorageConfig({
    ...localStorageConfig,
    [featureName]: true,
  });
};

const disableFeature = (
  featureName: keyof typeof CONFIG,
  localStorageConfig: ReturnType<typeof getLocalStorageEnvironmentConfig>,
) => {
  writeLocalStorageConfig(Object.fromEntries(
    Object.entries(localStorageConfig).filter(([key]) => key !== featureName),
  ));
};

const toggleFeature = (
  featureName: keyof typeof CONFIG,
  targetState: boolean,
  localStorageConfig: ReturnType<typeof getLocalStorageEnvironmentConfig>,
) => {
  if (isFeatureToggleEnabledByDefault(featureName, localStorageConfig)) {
    return noop;
  }
  if (targetState) {
    return enableFeature(featureName, localStorageConfig);
  }
  return disableFeature(featureName, localStorageConfig);
};

export const StandardFeatureToggle = ({
  featureName,
  label,
}: StandardFeatureToggleProps) => {
  const localStorageConfig = getLocalStorageEnvironmentConfig();
  const [isEnabled, setIsEnabled] = useState(isFeatureToggleEnabled(featureName, localStorageConfig));
  const changeHandler: ChangeEventHandler<HTMLInputElement> = useCallback(event => {
    setIsEnabled(event.target.checked);
  }, []);

  useEffect(() => {
    toggleFeature(featureName, isEnabled, getLocalStorageEnvironmentConfig());
  }, [featureName, isEnabled]);

  return (
    <TooltipComponent
      tooltipContent={
        isFeatureToggleEnabledByDefault(featureName, localStorageConfig) ?
          'This feature is enabled by default. You cannot disable it.' : ''
      }
    >
      <CheckboxComponent
        checkedSetter={changeHandler}
        isChecked={isEnabled}
        text={label}
        isDisabled={isFeatureToggleEnabledByDefault(featureName, localStorageConfig)}
      />
    </TooltipComponent>
  );
};
